import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, User, Building2 } from "lucide-react";
import { useLocation } from "wouter";

export default function TeamLoginPage() {
  const [, setLocation] = useLocation();
  const [salonSlug, setSalonSlug] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = trpc.team.login.useMutation({
    onSuccess: (data: any) => {
      window.localStorage.setItem("bb_access_mode", "team");
      toast.success("Login realizado com sucesso!");
      setLocation(data?.redirectPath ?? "/admin/weekly-calendar");
    },
    onError: (err) => {
      toast.error(err.message || "Usuário ou senha inválidos");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password.trim()) {
      toast.error("Informe seu e-mail ou usuário e a senha");
      return;
    }
    loginMutation.mutate({
      salonSlug: salonSlug.trim().toLowerCase() || undefined,
      identifier: identifier.trim().toLowerCase(),
      password,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-white text-2xl font-bold">B&B</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">Acesso de Profissionais e Equipe</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Use seu e-mail ou usuário e a senha cadastrados pelo salão. Não é necessário entrar com conta Manus.</p>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="salonSlug" className="text-sm font-medium text-gray-700">
                Código do Salão <span className="text-gray-400 font-normal">(somente se seu acesso for usado em mais de um salão)</span>
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="salonSlug"
                  type="text"
                  placeholder="opcional"
                  value={salonSlug}
                  onChange={(e) => setSalonSlug(e.target.value)}
                  className="pl-10"
                  autoComplete="organization"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                E-mail ou usuário
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="identifier"
                  type="text"
                  placeholder="voce@exemplo.com ou seu.usuario"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="pl-10"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 mt-2"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Acesso administrativo?{" "}
              <a href="/login" className="text-amber-600 hover:underline font-medium">
                Entrar como admin
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
